import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Clock,
  Loader2,
  Mail,
  Send,
  Trash2,
  UserCircle,
} from "lucide-react";

import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { useBusinesses } from "../../hooks/use-businesses";
import {
  Manager,
  PendingInvite,
  useInviteManager,
  useManagers,
  useRemoveManager,
} from "../../hooks/use-managers";
import SettingsSection from "./components/SettingsSection";

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const inviteSchema = yup.object({
  email: yup
    .string()
    .required("Enter the manager's email")
    .matches(EMAIL_REGEX, "Enter a valid email address"),
});

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Managers = () => {
  const { businessId } = useBusinesses();
  const { managers, pendingInvites, isLoading } = useManagers(businessId);
  const invite = useInviteManager(businessId);
  const remove = useRemoveManager(businessId);

  const [confirmRemove, setConfirmRemove] = useState<Manager | null>(null);

  const form = useForm<{ email: string }>({
    resolver: yupResolver(inviteSchema) as any,
    defaultValues: { email: "" },
    mode: "onTouched",
  });

  const onInvite = (values: { email: string }) => {
    invite.mutate(values.email, {
      onSuccess: () => form.reset(),
    });
  };

  const onConfirmRemove = () => {
    if (!confirmRemove) return;
    const userId = confirmRemove.userId._id;
    remove.mutate(userId, {
      onSuccess: () => setConfirmRemove(null),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[200px] w-full rounded-xl" />
        <Skeleton className="h-[300px] w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Invite a manager ── */}
      <SettingsSection
        title="Invite a manager"
        description="They'll get access once they create a DeeXoptions account with the email you invite."
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onInvite)}
            className="flex flex-col gap-3 mds:flex-row mds:items-end"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="min-w-0 flex-1">
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="manager@example.com"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toLowerCase())
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={invite.isPending}
              className="h-10 gap-2 rounded-xl px-5 text-[14px] font-semibold"
            >
              {invite.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Send invite
            </Button>
          </form>
        </Form>
        <p className="mt-3 text-[12px] font-medium text-muted-foreground">
          The invited person must sign up on DeeXoptions with this email to gain
          access.
        </p>
      </SettingsSection>

      {/* ── Active managers ── */}
      <SettingsSection
        title="Active managers"
        description="People who can manage this business."
      >
        {managers.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <UserCircle className="size-10 text-muted-foreground/40" />
            <p className="text-[14px] font-medium text-muted-foreground">
              No managers yet
            </p>
            <p className="text-[12px] text-muted-foreground">
              Invite someone above to get started.
            </p>
          </div>
        ) : (
          <ul className="divide-y">
            {managers.map((mgr) => {
              const user = mgr.userId;
              const firstName = user?.profile?.firstName ?? "";
              const lastName = user?.profile?.lastName ?? "";
              const initials =
                `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
                "M";
              const displayName =
                `${firstName} ${lastName}`.trim() || "Manager";
              const email =
                typeof user?.email === "string"
                  ? user.email
                  : user?.email?.address ?? "";
              return (
                <li
                  key={mgr._id}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <Avatar className="size-9">
                    <AvatarFallback className="bg-accent text-[12px] font-semibold text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-foreground">
                      {displayName}
                    </span>
                    {email && (
                      <span className="block truncate text-[12px] font-medium text-muted-foreground">
                        {email}
                      </span>
                    )}
                  </span>
                  <Badge
                    variant="outline"
                    className="shrink-0 border-emerald-200 bg-emerald-50 text-[11px] font-semibold text-emerald-700"
                  >
                    {mgr.status === "ACTIVE" ? "Active" : mgr.status}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmRemove(mgr)}
                    className="size-8 rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive"
                    title="Remove manager"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </SettingsSection>

      {/* ── Pending invites ── */}
      {pendingInvites.length > 0 && (
        <SettingsSection
          title="Pending invites"
          description="Invitations waiting for the recipient to sign up."
        >
          <ul className="divide-y">
            {pendingInvites.map((inv: PendingInvite) => (
              <li
                key={inv._id}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex size-9 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                  <Mail className="size-4" />
                </div>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold text-foreground">
                    {inv.invitedEmail}
                  </span>
                  <span className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
                    <Clock className="size-3" />
                    Expires {formatDate(inv.expiresAt)}
                  </span>
                </span>
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-200 bg-amber-50 text-[11px] font-semibold text-amber-700"
                >
                  Pending
                </Badge>
              </li>
            ))}
          </ul>
        </SettingsSection>
      )}

      {/* ── Remove confirmation dialog ── */}
      <Dialog
        open={!!confirmRemove}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
      >
        <DialogContent className="font-manrope sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Remove manager</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-semibold text-foreground">
                {confirmRemove?.userId?.profile?.firstName
                  ? `${confirmRemove.userId.profile.firstName} ${confirmRemove.userId.profile.lastName ?? ""}`.trim()
                  : "this manager"}
              </span>
              ? They will lose access to this business immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmRemove(null)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmRemove}
              disabled={remove.isPending}
              className="gap-2 rounded-xl"
            >
              {remove.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Managers;
